import React from 'react';
import { CellContext } from '@tanstack/react-table';
import cn from 'classnames';

import { Icons, SliderCheckbox } from '@components/common';
import Actions from '@components/common/Actions/Actions';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell';

import { dateFormattingWithSlash } from '@utils/date';
import { getDateWithoutOffset } from '@utils/kpi-utils/date';

import { IMiddlewareSMO } from '@interfaces/middlewareSMO/middlewareSMO';

import { ECrowdCellConnectionType } from '@enums/common';

import { columnSizes } from '@constants/table';
import {
	DELETE_DISABLED_TOOLTIP,
	DELETE_TEXT,
	DISABLE_PM_FILES_STORING,
	DISABLE_TEXT,
	ENABLE_PM_FILES_STORING,
	ENABLE_TEXT,
	MIDDLEWARE_TABS_NAMES,
} from './constants';

import styles from './ManageMiddlewareSMO.module.scss';
import tableStyles from '@components/common/Table/Table.module.scss';

interface IColumns {
	openModal: (middleware: IMiddlewareSMO | null, text: string) => void;
	openStorePMFiles: (middleware: IMiddlewareSMO | null) => void;
	isViewer?: boolean;
}

export const getColumns = ({
	openModal,
	openStorePMFiles,
	isViewer,
}: IColumns) => [
	{
		header: 'ID',
		accessorKey: 'systemId',
		cell: ({ getValue }: CellContext<IMiddlewareSMO, any>) => {
			const systemId = getValue();

			return <AdaptiveTableCell text={systemId} />;
		},
	},
	{
		header: 'Activation',
		accessorKey: 'actions',
		enableSorting: false,
		isHidden: isViewer,
		size: columnSizes.size85,
		cell: ({ row: { original } }: CellContext<IMiddlewareSMO, any>) => {
			const { isEnabled } = original;

			const onChange = () => openModal(original, isEnabled ? DISABLE_TEXT : ENABLE_TEXT);

			return (
				<SliderCheckbox
					checked={isEnabled}
					className={styles.actions}
					id={original?.id}
					onChange={onChange}
				/>
			);
		},
	},
	{
		header: 'STATUS',
		accessorKey: 'status',
		size: columnSizes.size85,
		enableSorting: false,
		cell: ({ getValue }: CellContext<IMiddlewareSMO, any>) => {
			const status = getValue();

			const className = status.name === ECrowdCellConnectionType.ONLINE
				? styles.online
				: status.name === ECrowdCellConnectionType.OFFLINE
					? styles.offline
					: undefined;

			return (
				<AdaptiveTableCell
					className={className}
					text={status.title}
				/>
			);
		},
	},
	{
		header: 'FIRST SEEN',
		accessorKey: 'createdAt',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IMiddlewareSMO, any>) => {
			const value = getValue();
			const dateWithoutOffset = getDateWithoutOffset(value);
			const date = dateFormattingWithSlash(dateWithoutOffset);

			return value && <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'LAST SEEN',
		accessorKey: 'lastSeen',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IMiddlewareSMO, any>) => {
			const value = getValue();
			const dateWithoutOffset = getDateWithoutOffset(value);
			const date = dateFormattingWithSlash(dateWithoutOffset);

			return value && <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Log PM File',
		enableSorting: false,
		accessorKey: 'logPmFiles',
		isHidden: isViewer,
		cell: ({ row: { original } }: CellContext<IMiddlewareSMO, any>) => {
			const { status, logPmFiles } = original;

			const isLogFile = logPmFiles;
			const isSetTimeDisabled = status.name === ECrowdCellConnectionType.OFFLINE;

			const labelClass = cn(styles.switchLabel, {
				[styles.switchLabelInactive]: !isViewer && isLogFile,
				[styles.switchLabelDisabled]: isViewer,
			});

			const onClick = () => {
				if (isViewer || isSetTimeDisabled) {
					return;
				}

				openModal(original, isLogFile ? DISABLE_PM_FILES_STORING : ENABLE_PM_FILES_STORING);
			};

			const handleLogPMFile = () => {
				if (isSetTimeDisabled) {
					return;
				}

				openStorePMFiles(original);
			};

			return (
				<div className={styles.switchWrapper}>
					<div className={styles.slider}>
						<SliderCheckbox
							checked={isLogFile}
							disabled={isSetTimeDisabled}
							id={original?.id}
							onChange={onClick}
						/>
					</div>
					<div
						className={styles.switchButtonWrapper}
						onClick={handleLogPMFile}
					>
						<span className={labelClass}>Set time</span>
						<span className={styles.switchIcon}>{Icons.viewIcon({ isDisabled: isSetTimeDisabled })}</span>
					</div>
				</div>

			);
		},
	},
	{
		header: () => <div className={tableStyles.centerColumnHeader}>Actions</div>,
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size85,
		isHidden: isViewer,
		cell: ({ row: { original } }: CellContext<IMiddlewareSMO, any>) => {
			const { hasAssociatedData, hasNetworkElements } = original;

			const handleDelete = () => openModal(original, DELETE_TEXT);

			const isDeleteDisabled = !hasAssociatedData && hasNetworkElements;
			const isDelete = !hasAssociatedData;

			return (
				<Actions
					deleteTooltip={isDeleteDisabled ? DELETE_DISABLED_TOOLTIP : undefined}
					gridNumberOfColumns={1}
					handleDelete={handleDelete}
					isDelete={isDelete}
					isDeleteDisable={isDeleteDisabled}
				/>
			);
		},
	},
];

export const getMiddlewareTabs = () => [
	{
		title: MIDDLEWARE_TABS_NAMES.middlewares,
		value: MIDDLEWARE_TABS_NAMES.middlewares,
		isVisible: true,
	},
	{
		title: MIDDLEWARE_TABS_NAMES.connectionLogs,
		value: MIDDLEWARE_TABS_NAMES.connectionLogs,
		isVisible: true,
	},
];
